import React from 'react';
import { getAvatarVariantUrl } from '../utils/avatarVariants';
import { BadgeTotemIcon, getBadgeTotemStage } from './BadgeTotem';
import { getShimmerClass, BADGE_STAR_POINTS } from '../utils/badgeUtils';
import './ProgressPortrait.css';

export function clampProgressPercent(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

// One filled outline avoids seams between adjacent stroked fragments.
function weeklyArcPath(progress) {
  if (!progress) return null;
  if (progress === 100) {
    return 'M50 2.3 A47.7 47.7 0 1 1 50 97.7 A47.7 47.7 0 1 1 50 2.3 Z M50 4.7 A45.3 45.3 0 1 1 50 95.3 A45.3 45.3 0 1 1 50 4.7 Z';
  }
  const outer = [];
  const inner = [];
  for (let i = 0; i <= 160; i += 1) {
    const fraction = i / 160;
    const angle = (-90 + 360 * progress / 100 * fraction) * Math.PI / 180;
    const width = 2.4 - fraction;
    const point = radius => `${(50 + radius * Math.cos(angle)).toFixed(4)},${(50 + radius * Math.sin(angle)).toFixed(4)}`;
    outer.push(point(46.5 + width / 2));
    inner.unshift(point(46.5 - width / 2));
  }
  return `M${outer.join(' L')} L${inner.join(' L')} Z`;
}

function portraitInitial(user = {}) {
  const identity = [user.display_name, user.email]
    .map(value => String(value || '').trim())
    .find(Boolean);
  return identity ? identity[0].toUpperCase() : '?';
}

const ProgressPortrait = ({
  user = {},
  progressPercent = null,
  progressState = null,
  monthPercent = null,
  yearPercent = null,
  starCount = null,
  visible = true,
  size = 'compact',
  className = '',
  status = null,
}) => {
  const progress = clampProgressPercent(progressPercent);

  const name = String(user.display_name || user.email || 'User').trim() || 'User';
  const variant = size === 'profile' ? 'profile' : size === 'roster' ? 'sm' : 'xs';
  const avatarUrl = getAvatarVariantUrl(user, variant);
  const statusText = ({ online: 'Online', offline: 'Offline', 'working-out': 'Working out' })[status] || null;
  const identityLabel = [name, statusText].filter(Boolean).join('. ');
  const resolvedProgressState = progressState || (progress === null ? 'unavailable' : 'ready');
  const ready = visible && resolvedProgressState === 'ready';
  const arcPath = ready ? weeklyArcPath(progress) : null;
  const month = ready && clampProgressPercent(monthPercent) !== null ? Number(monthPercent) : null;
  const finish = month === null ? '' : getShimmerClass(month).replace('shimmer shimmer-', '');
  const year = ready && size === 'profile' ? clampProgressPercent(yearPercent) : null;
  const totem = year === null ? null : getBadgeTotemStage(year);
  const stars = ready && size === 'profile' && Number.isFinite(starCount) ? Math.min(12, Math.max(0, Math.floor(starCount))) : 0;
  const detailLabel = [
    finish && `${finish === 'obsidian' ? 'Obsidian with platinum edge' : finish[0].toUpperCase() + finish.slice(1)} monthly finish`,
    totem && `${totem.label} annual totem`,
    stars > 0 && `${stars} earned ${stars === 1 ? 'star' : 'stars'}`,
  ].filter(Boolean).map(text => `${text}.`).join(' ');
  const label = !visible
    ? identityLabel
    : resolvedProgressState === 'loading'
      ? `${identityLabel}. Progress loading.`
      : !ready || progress === null
        ? `${identityLabel}. Progress not available.`
      : `${identityLabel}. ${progress}% 7-day plan compliance.`;

  return (
    <span className={`progress-portrait progress-portrait-${size} ${status ? `status-${status}` : ''} ${className}`.trim()} role="img" aria-label={[label, detailLabel].filter(Boolean).join(' ')}>
      {visible && (
        <svg className="progress-portrait-ring" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
          <circle className="progress-portrait-track" cx="50" cy="50" r="46.5" fill="none" strokeWidth="2.4" />
          {finish && <circle className="progress-portrait-finish" data-finish={finish} cx="50" cy="50" r="49.2" fill="none" strokeWidth="0.8" />}
          {arcPath && <path className="progress-portrait-week" d={arcPath} fillRule="evenodd" />}
        </svg>
      )}
      <span className="progress-portrait-photo">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="progress-portrait-image" />
        ) : (
          <span className="progress-portrait-placeholder" aria-hidden="true">{portraitInitial(user)}</span>
        )}
      </span>
      {totem && <span className="progress-portrait-seal" aria-hidden="true"><BadgeTotemIcon totemKey={totem.key} size={20} decorative /></span>}
      {stars > 0 && <svg className="progress-portrait-stars" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        {Array.from({ length: stars }, (_, index) => {
          const angle = (-90 + (index - (stars - 1) / 2) * 8) * Math.PI / 180;
          return <polygon className="progress-portrait-star" key={index} points={BADGE_STAR_POINTS} transform={`translate(${50 + 46.5 * Math.cos(angle)} ${50 + 46.5 * Math.sin(angle)}) scale(2.3)`} />;
        })}
      </svg>}
    </span>
  );
};

export default ProgressPortrait;
